import React, { useState, useEffect } from 'react';
import { getUsers, createUser, updateUser, deleteUser, getSupervisors } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Plus, Pencil, Trash2, Search, User } from 'lucide-react';
import { toast } from 'sonner';

const ROLES = [
  { value: 'mercaderista', label: 'Mercaderista' },
  { value: 'supervisor', label: 'Supervisor' },
  { value: 'admin', label: 'Administrador' }
];

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [supervisors, setSupervisors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterRole, setFilterRole] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone_e164: '',
    password: '',
    role: 'mercaderista',
    supervisor_id: ''
  });

  useEffect(() => {
    loadUsers();
    loadSupervisors();
  }, [filterRole]);

  const loadUsers = async () => {
    try {
      const params = {};
      if (filterRole && filterRole !== 'all') params.role = filterRole;
      const response = await getUsers(params);
      setUsers(response.data);
    } catch (error) {
      toast.error('Error cargando usuarios');
    } finally {
      setLoading(false);
    }
  };

  const loadSupervisors = async () => {
    try {
      const response = await getSupervisors();
      setSupervisors(response.data);
    } catch (error) {
      toast.error('Error cargando supervisores');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { ...formData };
      if (!data.supervisor_id) delete data.supervisor_id;
      if (editing && !data.password) delete data.password;

      if (editing) {
        await updateUser(editing.id, data);
        toast.success('Usuario actualizado');
      } else {
        await createUser(data);
        toast.success('Usuario creado');
      }
      setDialogOpen(false);
      resetForm();
      loadUsers();
      loadSupervisors();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error guardando usuario');
    }
  };

  const handleEdit = (user) => {
    setEditing(user);
    setFormData({
      name: user.name,
      email: user.email,
      phone_e164: user.phone_e164,
      password: '',
      role: user.role,
      supervisor_id: user.supervisor_id || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Desactivar este usuario?')) return;
    try {
      await deleteUser(id);
      toast.success('Usuario desactivado');
      loadUsers();
    } catch (error) {
      toast.error('Error desactivando usuario');
    }
  };

  const resetForm = () => {
    setEditing(null);
    setFormData({
      name: '',
      email: '',
      phone_e164: '',
      password: '',
      role: 'mercaderista',
      supervisor_id: ''
    });
  };

  const getRoleBadgeVariant = (role) => {
    switch (role) {
      case 'admin': return 'default';
      case 'supervisor': return 'secondary';
      default: return 'outline';
    }
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Chivo']">Usuarios</h1>
          <p className="text-slate-500">Gestiona los usuarios del sistema</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700" data-testid="add-user-btn">
              <Plus className="w-4 h-4 mr-2" /> Nuevo Usuario
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editing ? 'Editar Usuario' : 'Nuevo Usuario'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Nombre</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                  data-testid="user-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                  data-testid="user-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Teléfono (E.164)</Label>
                <Input
                  placeholder="+521234567890"
                  value={formData.phone_e164}
                  onChange={(e) => setFormData({...formData, phone_e164: e.target.value})}
                  required
                  data-testid="user-phone-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{editing ? 'Nueva Contraseña (dejar vacío para mantener)' : 'Contraseña'}</Label>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  required={!editing}
                  data-testid="user-password-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Rol</Label>
                <Select 
                  value={formData.role} 
                  onValueChange={(value) => setFormData({...formData, role: value})}
                >
                  <SelectTrigger data-testid="user-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLES.map(role => (
                      <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {formData.role === 'mercaderista' && (
                <div className="space-y-2">
                  <Label>Supervisor</Label>
                  <Select 
                    value={formData.supervisor_id || "none"} 
                    onValueChange={(value) => setFormData({...formData, supervisor_id: value === "none" ? "" : value})}
                  >
                    <SelectTrigger data-testid="user-supervisor-select">
                      <SelectValue placeholder="Seleccionar supervisor" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin supervisor</SelectItem>
                      {supervisors.map(sup => (
                        <SelectItem key={sup.id} value={sup.id}>{sup.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-testid="user-submit-btn">
                  {editing ? 'Actualizar' : 'Crear'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="pt-4">
          <Select value={filterRole} onValueChange={setFilterRole}>
            <SelectTrigger className="w-48" data-testid="filter-role">
              <SelectValue placeholder="Todos los roles" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los roles</SelectItem>
              {ROLES.map(role => (
                <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-4 space-y-2">
              {[...Array(5)].map((_, i) => <Skeleton key={`users-skeleton-${i}`} className="h-12" />)}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Nombre</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Teléfono</TableHead>
                  <TableHead>Rol</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-slate-400" />
                        <span className="font-medium">{user.name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-500">{user.email}</TableCell>
                    <TableCell className="font-mono text-sm">{user.phone_e164}</TableCell>
                    <TableCell>
                      <Badge variant={getRoleBadgeVariant(user.role)}>
                        {ROLES.find(r => r.value === user.role)?.label || user.role}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={user.active ? 'default' : 'secondary'}>
                        {user.active ? 'Activo' : 'Inactivo'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" onClick={() => handleEdit(user)}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(user.id)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {users.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                      No hay usuarios registrados
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
